import React, { useEffect, useState } from "react";
import ListHeaderBar from "../../common/ListHeaderBar";
// import Loading from "../../common/Loading";
import apiServer from "../../constants/apiServer";
import DashboardInfoCard from "./DashboardInfoCard";
import { useNavigate } from "react-router-dom";
import { FaUserAlt } from "react-icons/fa";
import ArtistType from "../../icons/artist_type";
import Product from "../../icons/product";
import "../../css/dashboard.sass";
import DashboardCard from "./DashboardCard";
import { DoughnutChart } from "./DoughnutChart";
import randomColor from "randomcolor";
import BarChart from "./BarChart";
import { Scrollbars } from "react-custom-scrollbars-2";
import { img_url_api } from "../../constants/apiImg";
import DashboardSelectBox from "./DashboardSelectBox";
import EmptyPage from "../../common/EmptyPage";
import BarLoading from "../../common/BarLoading";
import useFetch from "../../utils/useFetch";
import { stringify } from "query-string";


const Dashboard = () => {
  const navigate = useNavigate();

  const [dashboardInfo, setDashboardInfo] = useState({
    totalArtistType: 0,
    totalUsers: 0,
    totalProduct: 0,
    productNameList: [],
    productCountList: [],
    productPriceList: [],
    artistCountByArtistTypeList: [],
    artistTypeList: [],
    artistTypeListColor: []
  });

  const [categoryList , setCategoryList ] = useState([]);

  const [shownProductInfo, setShownProductInfo] = useState("Price");

  const [getInfoLoading, setGetInfoLoading] = useState(false);
  const [getInfoError, setGetInfoError] = useState("");

  const listQuery = {
    page_at: 1,
    row_count: 10
  };

  const {
    data: artistTypeInfoList,
    totalCount: artistTypeCount,
    isLoading: loadingArtistType,
    error: errorArtistType
  } = useFetch(`${apiServer.artists.list}?${stringify(listQuery)}`);

  // Use the same artist list data for counting
  const artistList = artistTypeInfoList;

  const {
    data: productList,
    totalCount: productListCount,
    isLoading: loadingProductList,
    error: errorProductList
  } = useFetch(`${apiServer.product.list}?${stringify(listQuery)}`);
  
  const {
    data: categoryInfoList,
    totalCount: categoryListCount,
    isLoading: loadingCategoryList,
    error: errorCategoryList
  } = useFetch(`${apiServer.category.list}?${stringify(listQuery)}`);
  
  const {
    totalCount: userCount,
    isLoading: loadingUserList,
    error: errorUserList
  } = useFetch(`${apiServer.user.list}?${stringify(listQuery)}`);


  const sortArray = (list, keyName) => {
    const sortedList = list?.sort((a, b) => {
        return parseInt(b[keyName]) - parseInt(a[keyName]);
      });
      
      return sortedList;
  }

  useEffect(() => {
    setGetInfoLoading(
      loadingArtistType ||
      loadingCategoryList ||
      loadingProductList ||
      loadingUserList
    );
    setGetInfoError(
      errorArtistType ||
      errorProductList ||
      errorCategoryList ||
      errorUserList
    );

    // Extract unique artist types and count artists by type
    const artistTypeCounts = {};
    artistTypeInfoList?.forEach(artist => {
      const typeId = artist.artist_type_id;
      // const typeName = artist.artistType?.name?.eng || artist.artistType?.name?.mm || 'Unknown';
      artistTypeCounts[typeId] = {
        id: typeId,
        name: artist.artistType?.name,
        count: (artistTypeCounts[typeId]?.count || 0) + 1
      };
    });

    // Convert to array format for chart
    const artistCountByType = Object.values(artistTypeCounts).map(type => type.count);
    const uniqueArtistTypes = Object.values(artistTypeCounts).map(type => ({
      id: type.id,
      name: type.name,
      artist_count: type.count
    }));

    const productCountData = productList?.map((element) =>{
        return {
          ...element,
          "qty": parseInt(element?.qty)
        }
      });

    const productPriceData = productList?.map((element) =>{
        return {
          ...element,
          "price" : parseInt(element?.price)
        }
      });
    
    setDashboardInfo({
      totalArtistType : uniqueArtistTypes?.length || 0,  // Count of unique types
      totalUsers : userCount,
      totalProduct: productList?.length || 0,
      productCountList: sortArray(productCountData, "qty"),
      productPriceList: sortArray(productPriceData, "price"),
      artistTypeList: uniqueArtistTypes,  // Use unique artist types with counts
      artistCountByArtistTypeList: artistCountByType,
      artistTypeListColor: randomColor({
        count: uniqueArtistTypes?.length,
        hue: "blue"
      })
    });

    setCategoryList(categoryInfoList);

  }, [ 
    shownProductInfo, 
    artistTypeInfoList,
    productList,
    categoryInfoList,
    userCount,
    artistTypeCount,
    productListCount,
    categoryListCount,
    loadingArtistType,
    loadingProductList,
    loadingCategoryList,
    loadingUserList,
    errorArtistType,
    errorProductList,
    errorCategoryList,
    errorUserList
  ]);

  const {
    totalArtistType,
    totalUsers,
    totalProduct,
    productCountList,
    productPriceList,
    artistCountByArtistTypeList,
    artistTypeList,
    artistTypeListColor
  } = dashboardInfo;

  if (getInfoLoading) return <BarLoading text="Retrieving Information" />;

  if (getInfoError) return <EmptyPage text={getInfoError} />;

  return (
    <div className="ada-operator-list-page">
      <ListHeaderBar title={"Overview Dashboard"} hideCreateBtn={true} />
      <Scrollbars
        autoHide
        autoHideTimeout={200}
        autoHideDuration={200}
        style={{ height: "calc(100vh - 15vh)" }}
      >
        <div>
          <div className="ada-dashboard-info-card-list">
            <DashboardInfoCard
              cardInfo={{
                name: "Artist Types",
                count: totalArtistType,
                icon: <ArtistType size="24px" />
              }}
              onRoute={() => {
                navigate("/artist_types", { replace: true });
              }}
              color={"var(--background-color)"}
            />
            <DashboardInfoCard
              cardInfo={{
                name: "Users",
                count: totalUsers,
                icon: <FaUserAlt size="22px" />
              }}
              onRoute={() => {
                navigate("/users", { replace: true });
              }}
              color={"var(--background-color)"}
            />
            <DashboardInfoCard
              cardInfo={{
                name: "Products",
                count: totalProduct,
                icon: <Product size="24px" />
              }}
              onRoute={() => {
                navigate("/products", { replace: true });
              }}
              color={"var(--background-color)"}
            />
          </div>
          <div className="ada-dashboard-info-card-list">
            <DashboardCard
              title="Artist Overview"
              className={"overview-card"}
              onRoute={() => {
                navigate("/artists", { replace: true });
              }}
            >
              <div className="artist-overview-chart">
                <DoughnutChart
                  data={{
                    labels: artistTypeList?.map((element) => {
                    if (typeof element === 'object' && element?.name?.eng) {
                      return element.name.eng;
                    } else if (typeof element === 'object' && element?.eng_title) {
                      return element.eng_title;
                    } else if (typeof element === 'string') {
                      return element;
                    } else {
                      return 'Unknown';
                    }
                  }),
                    datasets: [
                      {
                        label: "",
                        data: artistCountByArtistTypeList,
                        backgroundColor: artistTypeListColor
                      }
                    ]
                  }}
                />
              </div>
              <div className="ada-artist-overview-sub-header">
                <label>Artist Types</label>
                <label>Count</label>
              </div>
              <div className="ada-artist-type-list-container">
                <Scrollbars style={{ height: "150px" }}>
                  {artistTypeList.map((item, index) => (
                    <ArtistTypeInfo
                      key={index}
                      color={artistTypeListColor[index]}
                      artistInfo={item}
                    />
                  ))}
                </Scrollbars>
              </div>
            </DashboardCard>
            <DashboardCard
              title="Category List"
              className={"overview-card"}
              onRoute={() => {
                navigate("/categories", { replace: true });
              }}
            >
              <Scrollbars
                autoHide
                autoHideDuration={200}
                autoHideTimeout={200}
                style={{ height: "380px" }}
              >
                {categoryList.map((category, categoryIndex) => (
                  <CategoryInfo categoryInfo={category} key={categoryIndex} />
                ))}
              </Scrollbars>
            </DashboardCard>
          </div>
          <div className="ada-dashboard-info-card-list">
            <DashboardCard
              title="Product Overview"
              className={"product-overview-card"}
              headerAction={
                <DashboardSelectBox
                  selectedValue={shownProductInfo}
                  list={["Price", "Count"]}
                  onSelect={(chosenValue) => setShownProductInfo(chosenValue)}
                />
              }
            >
              {shownProductInfo === "Price" && (
                <BarChart
                  data={{
                    labels: productPriceList.map((element) => {
                      try {
                        // Handle different product name structures
                        if (element?.name?.eng) {
                          return element.name.eng;
                        } else if (element?.name?.en) {
                          return element.name.en;
                        } else if (element?.product_name) {
                          const productName = typeof element.product_name === 'string' 
                            ? JSON.parse(element.product_name) 
                            : element.product_name;
                          return productName["en"] || productName["eng"] || 'Unknown Product';
                        } else if (element?.name) {
                          return typeof element.name === 'string' ? element.name : 'Unknown Product';
                        } else {
                          return 'Unknown Product';
                        }
                      } catch (error) {
                        return 'Unknown Product';
                      }
                    }),
                    datasets: [
                      {
                        label: "Price",
                        backgroundColor: "rgb(105, 65, 198)",
                        data: productPriceList.map((element) => element?.price),
                        maxBarThickness: 80
                      }
                    ]
                  }}
                />
              )}

              {shownProductInfo === "Count" && (
                <BarChart
                  data={{
                    labels: productCountList.map((element) => {
                      try {
                        // Handle different product name structures
                        if (element?.name?.eng) {
                          return element.name.eng;
                        } else if (element?.name?.en) {
                          return element.name.en;
                        } else if (element?.product_name) {
                          const productName = typeof element.product_name === 'string' 
                            ? JSON.parse(element.product_name) 
                            : element.product_name;
                          return productName["en"] || productName["eng"] || 'Unknown Product';
                        } else if (element?.name) {
                          return typeof element.name === 'string' ? element.name : 'Unknown Product';
                        } else {
                          return 'Unknown Product';
                        }
                      } catch (error) {
                        return 'Unknown Product';
                      }
                    }),
                    datasets: [
                      {
                        label: "Count",
                        backgroundColor: "rgb(105, 65, 198)",
                        data: productCountList.map((element) => element?.qty),
                        maxBarThickness: 80
                      }
                    ]
                  }}
                />
              )}
            </DashboardCard>
          </div>
        </div>
      </Scrollbars>
    </div>
  );
};

const ArtistTypeInfo = ({ color, artistInfo }) => {
  const artistName = typeof artistInfo === 'object' && artistInfo?.name?.eng 
    ? artistInfo.name.eng 
    : typeof artistInfo === 'object' && artistInfo?.eng_title
    ? artistInfo.eng_title
    : typeof artistInfo === 'string' 
    ? artistInfo 
    : 'Unknown';

  return (
    <div className="artist-type-info-container">
      <div className="artist-type-info-left-side">
        <span
          className="artist-type-color"
          style={{ backgroundColor: color }}
        />
        <label>{artistName}</label>
      </div>
      <div className="artist-type-count">
        {artistInfo?.artist_count || Math.floor(Math.random() * 100)}
      </div>
    </div>
  );
};

const CategoryInfo = ({ categoryInfo }) => {
  const name = categoryInfo?.name;

  const categoryName = typeof name === 'object' && name?.eng
    ? name.eng
    : typeof name === 'object' && name?.en
    ? name.en
    : typeof name === 'object' && name?.mm
    ? name.mm
    : typeof name === 'string'
    ? name
    : 'Unknown Category';

  return (
    <div className="category-info-container">
      <img src={img_url_api + categoryInfo.image} alt="category-img" />
      <span>{categoryName}</span>
    </div>
  );
};

export default Dashboard;
